import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Minus, Plus } from "lucide-react";
import {
  LoadingState,
  ErrorState,
  EmptyState,
  StrengthBar,
  AskAboutThis,
  directionInfo,
  helpsHurts,
  humanise,
} from "@/components/research-hub";
import { useScenarioTrace } from "@/hooks/useResearchHubAnalyst";

type Row = Record<string, any>;

// The query the hook runs. Null until the user presses "Trace".
type TraceParams = { scenario: string; target?: string; maxHops?: number; minScore?: number };

// Best-effort: which input a set of "did you mean…" candidates refines. Honour a role/slot hint on
// the candidate payload if present, otherwise fall back to the caller's default.
function slotFromCandidates(candidates: Row[], fallback: "scenario" | "target"): "scenario" | "target" {
  for (const c of candidates) {
    if (c && typeof c === "object") {
      const role = String((c as Row).role ?? (c as Row).slot ?? (c as Row).kind ?? (c as Row).for ?? "").toLowerCase();
      if (role.includes("target")) return "target";
      if (role.includes("scenario")) return "scenario";
    }
  }
  return fallback;
}

export default function ScenarioTrace() {
  // Draft inputs the user is editing.
  const [scenarioDraft, setScenarioDraft] = useState("");
  const [targetDraft, setTargetDraft] = useState("");
  const [maxHops, setMaxHops] = useState(4);
  const [minScore, setMinScore] = useState(0);

  // The submitted query — only set when "Trace" is pressed, so nothing runs on first load.
  const [params, setParams] = useState<TraceParams | null>(null);

  const { data, isLoading, error, refetch } = useScenarioTrace(params);

  function runTrace(overrides?: Partial<TraceParams>) {
    const scenario = (overrides?.scenario ?? scenarioDraft).trim();
    if (!scenario) return;
    const target = (overrides?.target ?? targetDraft).trim();
    const next: TraceParams = {
      scenario,
      target: target || undefined,
      maxHops: overrides?.maxHops ?? maxHops,
      minScore: overrides?.minScore ?? minScore,
    };
    // Keep the visible inputs in step with whatever we trace (e.g. when a chip is clicked).
    setScenarioDraft(scenario);
    setTargetDraft(target);
    if (overrides?.maxHops != null) setMaxHops(overrides.maxHops);
    if (overrides?.minScore != null) setMinScore(overrides.minScore);
    setParams(next);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    runTrace();
  }

  // Steps / strength are live controls: once a trace is on screen, changing them re-runs THE TRACE
  // THAT IS SHOWING (the committed `params`), not whatever half-edited text sits in the inputs — so
  // nudging a control can never silently swap the traced scenario/target.
  function changeMaxHops(n: number) {
    setMaxHops(n);
    if (params) setParams({ ...params, maxHops: n });
  }
  function changeMinScore(v: number) {
    setMinScore(v);
    if (params) setParams({ ...params, minScore: v });
  }

  // Pick a candidate from a "did you mean…" chip: drop it into the scenario or target
  // input (whichever it relates to) and re-run.
  function chooseCandidate(label: string, slot: "scenario" | "target") {
    if (!label) return;
    if (slot === "target") runTrace({ target: label });
    else runTrace({ scenario: label });
  }

  const resolveError =
    data?.error === "scenario_not_resolved" || data?.error === "target_not_resolved" ? data.error : null;
  // Any other truthy error value from the trace is a real failure — surface it calmly instead of
  // letting it fall through to a misleading "no paths" empty.
  const otherError = data?.error && !resolveError ? String(data.error) : null;
  const ambiguity: Row[] = Array.isArray(data?.ambiguity) ? data!.ambiguity! : [];
  // Which input the candidates belong to: a target-resolution failure is about the target.
  const candidateSlot: "scenario" | "target" = data?.error === "target_not_resolved" ? "target" : "scenario";
  // On the success path the error doesn't tell us the slot — prefer a hint on the candidate payload,
  // else refine the target when the user supplied one (the scenario already resolved), else scenario.
  const successSlot: "scenario" | "target" = slotFromCandidates(ambiguity, targetDraft.trim() ? "target" : "scenario");

  const paths: Row[] = Array.isArray(data?.paths) ? [...data!.paths!] : [];
  paths.sort((a, b) => (b?.propagation_score ?? 0) - (a?.propagation_score ?? 0));

  const scenarioLabel = data?.scenario?.label ?? params?.scenario ?? "";
  const targetLabel = data?.target?.label ?? "";

  // No paths is a normal "no route found" answer (whether `reachable` is false, true with a strict
  // threshold, or simply absent) — show the recovery empty, not a bare "no paths" line.
  const noRoute = !resolveError && !otherError && paths.length === 0;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="space-y-1">
        <h1 className="font-heading text-xl font-semibold">Trace a scenario</h1>
        <p className="font-body text-sm text-muted-foreground">
          Follow how a scenario would ripple through to a target, step by step.
        </p>
      </div>

      {/* Controls */}
      <Card className="p-4 space-y-4">
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1 block">
              <span className="font-body text-xs text-muted-foreground">Scenario</span>
              <Input
                value={scenarioDraft}
                onChange={(e) => setScenarioDraft(e.target.value)}
                placeholder="e.g. oil price spike"
                className="font-body"
              />
            </label>
            <label className="space-y-1 block">
              <span className="font-body text-xs text-muted-foreground">Target (optional)</span>
              <Input
                value={targetDraft}
                onChange={(e) => setTargetDraft(e.target.value)}
                placeholder="e.g. UK inflation"
                className="font-body"
              />
            </label>
          </div>

          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-6 flex-wrap">
              <Stepper
                label="Steps allowed"
                value={maxHops}
                min={1}
                max={8}
                onChange={changeMaxHops}
                suffix={maxHops === 1 ? "step" : "steps"}
              />
              <div className="space-y-1">
                <span className="font-body text-xs text-muted-foreground block">Strength threshold</span>
                <div className="flex items-center gap-1">
                  {([
                    { v: 0, label: "Any" },
                    { v: 0.33, label: "Tentative+" },
                    { v: 0.66, label: "Strong only" },
                  ] as const).map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => changeMinScore(opt.v)}
                      className={
                        "rounded-md border px-2.5 py-1 font-body text-xs transition-colors " +
                        (Math.abs(minScore - opt.v) < 0.001
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border text-muted-foreground hover:bg-muted")
                      }
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <Button type="submit" disabled={!scenarioDraft.trim()}>
              <Search className="h-4 w-4 mr-2" aria-hidden /> Trace
            </Button>
          </div>
        </form>
      </Card>

      {/* Results region */}
      {params == null ? (
        <EmptyState
          title="Enter a scenario to begin."
          body="Type a scenario above — and a target if you have one in mind — then press Trace."
        />
      ) : isLoading ? (
        <LoadingState rows={3} />
      ) : error ? (
        <ErrorState onRetry={() => refetch()} />
      ) : resolveError ? (
        <DidYouMean
          slot={candidateSlot}
          candidates={ambiguity}
          onChoose={chooseCandidate}
          typed={candidateSlot === "target" ? targetDraft : scenarioDraft}
        />
      ) : otherError ? (
        <EmptyState
          title="We couldn't trace that scenario."
          body="Something about that scenario didn't work. Try describing it a different way."
        />
      ) : (
        <div className="space-y-5">
          {/* What we resolved to */}
          <div className="space-y-1">
            <p className="font-body text-sm">
              {targetLabel ? (
                <>
                  From <span className="font-medium text-foreground">{scenarioLabel}</span> to{" "}
                  <span className="font-medium text-foreground">{targetLabel}</span>
                </>
              ) : (
                <>
                  Tracing from <span className="font-medium text-foreground">{scenarioLabel}</span>
                </>
              )}
            </p>
          </div>

          {/* "Did you mean…" chips still surface when candidates are present, even on success. */}
          {ambiguity.length > 0 && (
            <DidYouMean slot={successSlot} candidates={ambiguity} onChoose={chooseCandidate} compact />
          )}

          {noRoute ? (
            <EmptyState
              title="No causal path found at this strength."
              body="Try lowering the strength threshold or allowing more steps."
              action={
                <div className="flex items-center gap-2 flex-wrap justify-center">
                  {minScore > 0 && (
                    <Button variant="outline" size="sm" onClick={() => runTrace({ minScore: 0 })}>
                      Lower the threshold
                    </Button>
                  )}
                  {maxHops < 8 && (
                    <Button variant="outline" size="sm" onClick={() => runTrace({ maxHops: Math.min(8, maxHops + 2) })}>
                      Allow more steps
                    </Button>
                  )}
                </div>
              }
            />
          ) : (
            <div className="space-y-3">
              {paths.map((p, i) => (
                <PathCard key={i} path={p} scenarioLabel={scenarioLabel} targetLabel={targetLabel} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ stepper -- */
function Stepper({
  label,
  value,
  min,
  max,
  onChange,
  suffix,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
  suffix?: string;
}) {
  return (
    <div className="space-y-1">
      <span className="font-body text-xs text-muted-foreground block">{label}</span>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-7 w-7"
          disabled={value <= min}
          onClick={() => onChange(Math.max(min, value - 1))}
          aria-label="Fewer"
        >
          <Minus className="h-3.5 w-3.5" aria-hidden />
        </Button>
        <span className="font-body text-sm w-20 text-center tabular-nums">
          {value} {suffix}
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-7 w-7"
          disabled={value >= max}
          onClick={() => onChange(Math.min(max, value + 1))}
          aria-label="More"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- did you mean -- */
function DidYouMean({
  slot,
  candidates,
  onChoose,
  typed,
  compact = false,
}: {
  slot: "scenario" | "target";
  candidates: Row[];
  onChoose: (label: string, slot: "scenario" | "target") => void;
  typed?: string;
  compact?: boolean;
}) {
  // Only candidates carrying a readable label are clickable.
  const chips = candidates
    .map((c) => (typeof c === "string" ? c : c?.label ?? c?.name ?? c?.title ?? ""))
    .filter((s): s is string => typeof s === "string" && s.trim().length > 0);

  if (compact && chips.length === 0) return null;

  const heading = typed
    ? `We couldn't find "${typed}" — did you mean…`
    : "We couldn't find that — did you mean…";

  if (chips.length === 0) {
    // Resolution failed but nothing to suggest.
    return (
      <EmptyState
        title="We couldn't find that."
        body={`Try a different ${slot === "target" ? "target" : "scenario"}, or describe it another way.`}
      />
    );
  }

  return (
    <Card className={compact ? "p-3 space-y-2" : "p-5 space-y-3"}>
      <p className="font-body text-sm text-muted-foreground">{compact ? "Did you mean…" : heading}</p>
      <div className="flex flex-wrap gap-2">
        {chips.map((label, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onChoose(label, slot)}
            className="rounded-full border border-primary/30 bg-primary/5 px-3 py-1 font-body text-sm text-primary hover:bg-primary/10 transition-colors"
          >
            {label}
          </button>
        ))}
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------- a path -- */
function PathCard({
  path,
  scenarioLabel,
  targetLabel,
}: {
  path: Row;
  scenarioLabel: string;
  targetLabel: string;
}) {
  const labels: any[] = Array.isArray(path.path_labels) ? path.path_labels : [];
  const edgeTypes: any[] = Array.isArray(path.path_edge_types) ? path.path_edge_types : [];
  const edgeDirs: any[] = Array.isArray(path.path_edge_dirs) ? path.path_edge_dirs : [];

  const net = helpsHurts(path.net_sign);
  const strengthValue = typeof path.net_magnitude === "number" ? path.net_magnitude : path.propagation_score;

  const hops = path.hop_distance;
  const conflict = path.conflict_flag === true;

  // The propagate RPC returns a flat `evidence_source_ids` per path (not `per_hop_…`). Accept either
  // shape and flatten defensively.
  const rawSources = path.evidence_source_ids ?? path.per_hop_evidence_source_ids;
  const sources = Array.isArray(rawSources) ? rawSources.flat().filter(Boolean).length : null;

  // Build a sentence for the "Ask about this" prefill from labels only — never an id.
  const fromName = labels[0] ?? scenarioLabel ?? "this scenario";
  const toName = labels[labels.length - 1] ?? targetLabel ?? "the target";
  const askPrefill =
    net.word && net.word !== "mixed"
      ? `How does ${fromName} ${net.word} ${toName}? Walk me through the chain.`
      : `How does ${fromName} flow through to ${toName}? Walk me through the chain.`;

  return (
    <Card className="p-4 space-y-3">
      {/* Left-to-right arrow diagram: node → (type, dir) → node → … */}
      <div className="flex items-center gap-x-1.5 gap-y-1 flex-wrap">
        {labels.length === 0 ? (
          <span className="font-body text-sm text-muted-foreground">Path detail unavailable.</span>
        ) : (
          labels.map((node: any, idx: number) => {
            const nodeName = typeof node === "string" ? node : node?.label ?? node?.name ?? humanise(String(node));
            const isLast = idx === labels.length - 1;
            const edgeType = edgeTypes[idx];
            const dir = directionInfo(edgeDirs[idx]);
            return (
              <span key={idx} className="inline-flex items-center gap-x-1.5">
                <span className="font-body text-sm font-medium text-foreground whitespace-nowrap">{nodeName}</span>
                {!isLast && (
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <span className="font-body text-xs">
                      {dir.glyph && <span className="mr-0.5">{dir.glyph}</span>}
                      {edgeType != null ? humanise(String(edgeType)) : "leads to"}
                    </span>
                    <span aria-hidden className="text-primary">
                      →
                    </span>
                  </span>
                )}
              </span>
            );
          })
        )}
      </div>

      {/* Net read on this path */}
      <div className="flex items-center gap-3 flex-wrap">
        {net.word && (
          <span
            className={
              "font-body text-sm font-medium " +
              (net.tone === "good" ? "text-primary" : net.tone === "bad" ? "text-destructive" : "text-foreground")
            }
          >
            On balance, {net.word === "mixed" ? "a mixed effect" : `this ${net.word}`}
          </span>
        )}
        <StrengthBar value={strengthValue} label="how strongly" />
        {hops != null && (
          <span className="font-body text-xs text-muted-foreground">
            {hops} step{hops === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {/* Quiet footnotes */}
      <div className="flex items-center gap-3 flex-wrap">
        {sources != null && sources > 0 && (
          <span className="font-body text-[11px] text-muted-foreground">
            {sources} source{sources === 1 ? "" : "s"}
          </span>
        )}
        {conflict && (
          <span className="font-body text-[11px] text-[hsl(var(--financial-gold))]">crosses a disputed link</span>
        )}
        <AskAboutThis prefill={askPrefill} className="ml-auto" />
      </div>
    </Card>
  );
}
